import React, { useContext, useMemo } from 'react'
import ImageGallery from 'components/Luxkit/Carousel/ImageGallery'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import styled from 'styled-components'
import { rem } from 'polished'
import ProductTypeLabel from 'components/Luxkit/Label/ProductTypeLabel'
import OfferBadge from 'components/Common/OfferBadge'
import { isBedbank, isLEOffer } from 'lib/offer/offerTypes'
import { mediaQueryUp } from 'components/utils/breakpoint'
import variables from 'styles/vendor/bootstrap/variables'
import { ImageCarouselInterstitial } from 'components/Luxkit/Carousel/BaseImageCarousel'
import VimeoVideo from 'components/Common/Video/VimeoVideo'
import Clickable from 'components/Common/Clickable'
import LightBoxContext from 'components/Luxkit/Modal/LightBoxContext'
import VideoLightbox from 'components/Luxkit/Carousel/VideoLightbox'
import LinePlayIcon from 'components/Luxkit/Icons/line/LinePlayIcon'
import OfferPartnershipLabel from 'components/Common/Labels/OfferPartnershipLabel'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import { isLPCTacticalEnabled } from 'lib/offer/offerUtils'
import { OptimizelyClientSideExperiments, useOptimizelyExperiment } from 'lib/optimizely/optimizelyUtils'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import ShareModal from 'components/Common/Modals/ShareModal'
import { getShareModalConfig } from 'lib/customer/getShareModalConfig'
import LineShareAltIcon from 'components/Luxkit/Icons/line/LineShareAltIcon'
import Group from 'components/utils/Group'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import IconButton from 'components/Luxkit/Button/IconButton'

const PlayButton = styled.div`
  position: absolute;
  left: 50%;
  top: 50%;
  transition: transform 0.2s;
  transform: translateY(-50%) translateX(-50%) scale(1);
  border-radius: ${props => props.theme.borderRadius.round};
  background-color: ${props => props.theme.palette.neutral.eight};
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${rem(80)};
  width: ${rem(80)};

  >:first-child {
    height: ${rem(48)};
    width: ${rem(48)};
  }

  ${mediaQueryUp.tablet} {
    height: ${rem(108)};
    width: ${rem(108)};

    >:first-child {
      height: ${rem(60)};
      width: ${rem(60)};
    }
  }
`

const VimeoWrapper = styled(Clickable)`
  height: 100%;
  width: 100%;
  position: relative;
  background-color: ${props => props.theme.palette.neutral.one};

  > * {
    pointer-events: none;
  }

  &:focus, &:hover {
    > ${PlayButton} {
      transform: translateY(-50%) translateX(-50%) scale(1.2);
    }
  }
`

const MobileHuggingContainer = styled(LayoutContainer)`
  padding: 0;

  ${mediaQueryUp.tablet} {
    padding-right: ${rem(variables.gutterWidth / 2)};
    padding-left: ${rem(variables.gutterWidth / 2)};
  }
`

const OfferOverlayContainer = styled(LabelGroup)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`

const GalleryButtons = styled(CSSBreakpoint)`
  display: flex;
  pointer-events: auto;
`

interface Props {
  offer: App.Offer | App.BedbankOffer;
  shareModalConfig?: ReturnType<typeof getShareModalConfig>
}

function OfferGallery(props: Props) {
  const { offer, shareModalConfig } = props
  const canParticipateInSaveButtonExperiment = offer.type === 'hotel' && offer.property?.isUltraLux !== true
  const isSaveButtonExperimentEnabled = !!useOptimizelyExperiment(OptimizelyClientSideExperiments.tripOfferPageSaveButton, canParticipateInSaveButtonExperiment)

  const hideProductType = (offer as App.Offer).isPartnerProperty || (isBedbank(offer) && offer.promotions.length > 0)
  const showLightbox = useContext(LightBoxContext)
  const hasTactical = isLPCTacticalEnabled() && ((offer as App.Offer)?.hasTactical || false)

  const interstitials: Array<ImageCarouselInterstitial> | undefined = useMemo(() => {
    if (isLEOffer(offer) && offer.vimeoVideoId) {
      return [{
        id: 'video',
        position: 2,
        element: <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId={offer.vimeoVideoId} playOnMount />)}>
          <VimeoVideo vimeoVideoId={offer.vimeoVideoId} controls={false} />
          <PlayButton>
            <LinePlayIcon />
          </PlayButton>
        </VimeoWrapper>,
      }]
    }

    if (isBedbank(offer) && offer.videos?.length > 0 && offer.videos?.find(video => video.type === 'vimeo')) {
      return offer.videos.filter(video => video.type === 'vimeo').map((video, index) => ({
        id: 'video',
        position: 2 + index,
        element: <VimeoWrapper onClick={() => showLightbox(<VideoLightbox videoId={video.id} playOnMount />)}>
          <VimeoVideo vimeoVideoId={video.id} controls={false} />
          <PlayButton>
            <LinePlayIcon />
          </PlayButton>
        </VimeoWrapper>,
      }))
    }

    return undefined
  }, [offer, showLightbox])

  return <Pane>
    <MobileHuggingContainer>
      <ImageGallery
        topLeft={<OfferOverlayContainer>
          {!hideProductType && !hasTactical &&
            <ProductTypeLabel productType={offer.productType} />
          }
          {hasTactical &&
            <ProductTypeLabel productType="limited_time_special"/>
          }
          {isLEOffer(offer) &&
            <OfferPartnershipLabel offer={offer} />
          }
        </OfferOverlayContainer>}
        topRight={<>
          {offer.badge && <OfferBadge badge={offer.badge} />}
          {isSaveButtonExperimentEnabled && shareModalConfig && (<GalleryButtons max="mobile">
            <Group direction="horizontal" gap={8}>
              { shareModalConfig &&
                <ShareModal shareConfig={shareModalConfig}>
                  <IconButton
                    shape="circle"
                    kind="secondary"
                    variant="dark"
                    size="medium"
                  >
                    <LineShareAltIcon/>
                  </IconButton>
                </ShareModal>
            }
              <TripGuard>
                <BookmarkButton offer={offer} iconOnly/>
              </TripGuard>
            </Group>
          </GalleryButtons>
          )}
        </>
        }
        interstitials={interstitials}
        images={offer.images}
        eagerLoadFirstImage
        videoId={(offer as any).vimeoVideoId}
        arrowBehaviour="always-visible"
      />
    </MobileHuggingContainer>
  </Pane>
}

export default OfferGallery
